import {
  ESIGN_ACTIVITY_KIND,
  ESIGN_ACTIVITY_LABEL,
} from '@/constants/esignature';
import type { EsignActivityPayload } from '@/types/esignature';

/**
 * Keys whose presence tells the backend that the recipient section was actually
 * edited. `multipart/form-data` drops empty arrays in transit, so an absent
 * `cc_user_ids` is ambiguous — it cannot be told apart from "the recruiter never
 * touched CC". The backend resolves it by reading `cc_user_ids` as *cleared*
 * only when one of these also arrives, and as *untouched* otherwise.
 *
 * The wizard always submits the whole form, so `custom_message_per_recipient`
 * and `employer_signer_required` are on every request and clearing the last CC
 * user works. This constant exists so the invariant is checked, not assumed:
 * `appendEsignFormData` throws if an empty CC list is sent without one.
 */
export const CC_CLEAR_SENTINEL_KEYS = [
  'custom_message_per_recipient',
  'employer_signer_required',
  'company_signer_id',
  'cc_email_subject',
  'cc_email_body',
] as const;

const appendIfDefined = (
  formData: FormData,
  key: string,
  value: unknown
): void => {
  if (value === undefined || value === null) return;
  formData.append(key, String(value));
};

/**
 * Builds the multipart body for creating or updating an e-sign stage activity.
 *
 * Two behaviours are deliberate and load-bearing:
 *
 * 1. **Documents are never sent empty.** An empty `esign_documents[]` arrives as
 *    an absent key, which the backend reads as "documents untouched" — so a
 *    "remove everything" request would be a silent 200 no-op rather than an
 *    error. Zero documents is invalid anyway, so this throws instead of sending
 *    a request that would appear to succeed. The wizard blocks it in the UI
 *    first; this is the backstop.
 * 2. **The employer-signer flag is translated here, once.** The wizard reuses the
 *    offer composer, whose form field is `company_signer_required`; the
 *    stage-action API's field is `employer_signer_required`. These are NOT the
 *    same field under two spellings — the backend also has its own
 *    `company_signer_required`, which is the account's global default offer
 *    envelope and unrelated to this. Do not "fix" one into the other; the
 *    translation belongs here, at the boundary, and nowhere else.
 */
export const appendEsignFormData = (
  formData: FormData,
  payload: EsignActivityPayload,
  { isCreate = false }: { isCreate?: boolean } = {}
): FormData => {
  const {
    label,
    position,
    documents = [],
    company_signer_required,
    signing_order,
    email_subject,
    email_body,
    company_signer_id,
    custom_message_per_recipient,
    company_email_subject,
    company_email_body,
    cc_email_subject,
    cc_email_body,
    cc_user_ids,
  } = payload;

  if (documents.length === 0) {
    throw new Error(
      'esign activity requires at least one document — an empty esign_documents[] is dropped by multipart and would silently no-op'
    );
  }

  if (isCreate) {
    formData.append('kind', ESIGN_ACTIVITY_KIND);
  }
  formData.append('label', label || ESIGN_ACTIVITY_LABEL);
  appendIfDefined(formData, 'position', position);

  // New uploads go as Files; documents already on the activity go as bare ids,
  // which is how the backend knows to retain rather than replace them.
  documents.forEach((document) => {
    if (document.file instanceof File) {
      formData.append('esign_documents[]', document.file);
      return;
    }
    if (document.id !== undefined && document.id !== null) {
      formData.append('esign_documents[]', String(document.id));
    }
  });

  formData.append(
    'employer_signer_required',
    String(!!company_signer_required)
  );
  if (company_signer_required && company_signer_id !== undefined) {
    formData.append('company_signer_id', String(company_signer_id));
  }
  if (company_signer_required) {
    appendIfDefined(formData, 'signing_order', signing_order);
  }

  formData.append(
    'custom_message_per_recipient',
    String(!!custom_message_per_recipient)
  );
  formData.append('email_subject', email_subject || '');
  formData.append('email_body', email_body || '');

  // Per-recipient blocks are only meaningful when the toggle is on and that
  // recipient exists, matching how the offer composer builds its payload.
  const ccUserIds = Array.isArray(cc_user_ids) ? cc_user_ids : [];
  if (custom_message_per_recipient && company_signer_required) {
    formData.append('company_email_subject', company_email_subject || '');
    formData.append('company_email_body', company_email_body || '');
  }
  if (custom_message_per_recipient && ccUserIds.length > 0) {
    formData.append('cc_email_subject', cc_email_subject || '');
    formData.append('cc_email_body', cc_email_body || '');
  }

  ccUserIds.forEach((id) => {
    formData.append('cc_user_ids[]', String(id));
  });

  if (ccUserIds.length === 0) {
    const hasSentinel = CC_CLEAR_SENTINEL_KEYS.some((key) => formData.has(key));
    if (!hasSentinel) {
      throw new Error(
        'clearing cc_user_ids needs one of the recipient-section keys in the same request, or the backend keeps the persisted list'
      );
    }
  }

  return formData;
};

export const getEsignFormData = (
  payload: EsignActivityPayload,
  options?: { isCreate?: boolean }
): FormData => appendEsignFormData(new FormData(), payload, options);

export const getValidateEsignDocumentsFormData = ({
  files = [],
  applicationId,
  activityId,
  documentIds = [],
}: {
  files?: File[];
  applicationId?: string | number;
  /** The activity the saved documents belong to; absent for an ad-hoc send. */
  activityId?: string | number;
  /** Saved documents to check against the candidate, by attachment id. */
  documentIds?: (string | number)[];
}): FormData => {
  const formData = new FormData();
  files.forEach((file) => formData.append('esign_documents[]', file));
  // With a candidate in hand the server also checks candidate.* tokens for empty values.
  if (applicationId) {
    formData.append('ats_application_id', String(applicationId));
  }
  documentIds.forEach((id) =>
    formData.append('esign_document_ids[]', String(id))
  );
  if (documentIds.length && activityId) {
    formData.append('ats_job_stage_action_id', String(activityId));
  }
  return formData;
};
